import '@Components/ScenarioPlanningChart/ScenarioPlanningTooltip.scss';
import * as R from 'ramda';
import { ReactElement } from 'react';
import { MdCalendarToday } from 'react-icons/md';
import {
  getScenarioMetricLabel,
  MODEL_TYPES,
  SCENARIO_METRIC_ENUM
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';
import { PercentChange } from '@Components/PercentChange';
import {
  abbreviateScenarioValue,
  ScenarioPlanningChartEntry,
  buildHeaderLabel,
  buildSparkChartData,
  computeDeltaPercent,
  getSegmentLayout,
  isMonetaryMetric,
  SCENARIO_NEGATIVE_PILL_COLOR
} from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { FlightingSparkChart } from '@Components/ScenarioPlanningChart/FlightingSparkChart';
import { LimitedSizeType, Size, StretchMode } from '@Components/designConstants';

interface ScenarioPlanningTooltipProps {
  outcomeVariable?: string;
  payload?: { payload: ScenarioPlanningChartEntry }[];
}

export const ScenarioPlanningTooltip = (
  props: ScenarioPlanningTooltipProps
): ReactElement | null => {
  const { outcomeVariable, payload } = props;
  if (R.isNil(payload) || !payload.length) {
    return null;
  }
  const data = payload[0].payload;
  const {
    baselineValue,
    color,
    confidenceInterval,
    contributingModels,
    directValue,
    hasScenario,
    id,
    indirectValue,
    isDemandCapture,
    flightingData,
    metric,
    modelType,
    actualizedValue,
    showActualized,
    showBreakout,
    showPercentageView,
    totalValue,
    useHistoricalFlighting
  } = data;
  const headerLabel = buildHeaderLabel(id ?? '');
  const getConfidenceIntervalArr = (): number[] | undefined => {
    if (!confidenceInterval) {
      return undefined;
    }
    return Array.isArray(confidenceInterval)
      ? confidenceInterval
      : [confidenceInterval, confidenceInterval];
  };
  const confidenceIntervalArr = getConfidenceIntervalArr();
  // Percentage view: show each value as a share of the chart-wide grand total instead of its raw
  // dollar/count amount — matches the bar chart's on-bar labels (ScenarioPlanningChart.tsx).
  const valueFormatter = (val: number): string =>
    showPercentageView && totalValue
      ? `${Math.round((val / totalValue) * 100)}%`
      : abbreviateScenarioValue(val, { monetary: isMonetaryMetric(metric) });
  const metricName = getScenarioMetricLabel(metric, outcomeVariable);
  const displayedChange = showActualized
    ? actualizedValue - baselineValue
    : directValue - baselineValue;

  const makePercentPill = (change: number, base: number, size: LimitedSizeType = Size.SM) => (
    <PercentChange
      stretchMode={StretchMode.FIT}
      negativeColor={SCENARIO_NEGATIVE_PILL_COLOR}
      percentChange={computeDeltaPercent(change, base, metric)}
      size={size}
    />
  );

  const header = (
    <div className='tooltip-header'>
      <div className='header-label'>{headerLabel}</div>
      {isDemandCapture !== undefined && isDemandCapture !== null && (
        <div className='header-pill'>
          {isDemandCapture ? 'Demand Capture' : 'Demand Generation'}
        </div>
      )}
      {/* ==== HROAS: ScenarioPlanning.tsx only populates contributingModels for Channel/Platform/
      Tactic grouping (empty for Model grouping, where the bar's own label already names the
      model) — so this renders whenever it's non-empty, including a single source. Channel/
      Platform/Tactic buckets often blend to a single model per bucket rather than 2+ (rMMM and
      Brand Equity frequently share a channel NAME but land in separate buckets), so without a
      singular case there'd be no way to tell those bars apart. ==== */}
      {contributingModels && contributingModels.length >= 1 && (
        <div className='header-models'>
          {contributingModels.length === 1 ? 'Source' : 'Sources'}:{' '}
          {contributingModels.map(model => model.label).join(' + ')}
        </div>
      )}
    </div>
  );

  const body = showBreakout ? (
    <div className='body'>
      <div className='actualized-row primary'>
        <div className='row-left'>
          <div className='row-icon'>
            <div className='actualized-icon' />
          </div>
          <div className='row-label'>Actualized {metricName}</div>
        </div>
        <div className='row-right'>
          <div className='row-values'>
            <div className='row-value'>{valueFormatter(actualizedValue)}</div>
            {confidenceIntervalArr && (
              <div className='confidence-interval'>
                CI: {valueFormatter(actualizedValue - confidenceIntervalArr[0])}–
                {valueFormatter(actualizedValue + confidenceIntervalArr[1])}
              </div>
            )}
          </div>
          {hasScenario && (
            <div className='row-pill'>{makePercentPill(displayedChange, baselineValue)}</div>
          )}
        </div>
      </div>
      <div className='actualized-row secondary'>
        <div className='row-left'>
          <div className='row-icon'>
            <div className='solid-icon' style={{ backgroundColor: color }} />
          </div>
          <div className='row-label'>{metricName}</div>
        </div>
        <div className='row-right'>
          <div className='row-values'>
            <div className='row-value'>{valueFormatter(directValue)}</div>
            {confidenceIntervalArr && (
              <div className='confidence-interval'>
                CI: {valueFormatter(directValue - confidenceIntervalArr[0])}–
                {valueFormatter(directValue + confidenceIntervalArr[1])}
              </div>
            )}
          </div>
          {hasScenario && (
            <div className='row-pill'>
              {makePercentPill(directValue - baselineValue, baselineValue)}
            </div>
          )}
        </div>
      </div>
      <div className='actualized-row secondary'>
        <div className='row-left'>
          <div className='row-icon'>
            <svg className='hatched-icon' viewBox='0 0 16 16'>
              <rect
                width='16'
                height='16'
                rx='2'
                fill={`url(#shadedStripe${R.defaultTo(data.label, id)?.replace(/ /g, '')})`}
              />
            </svg>
          </div>
          <div className='row-label'>
            {isDemandCapture ? 'Credit to Demand Generation' : 'Credit from Demand Capture'}
          </div>
        </div>
        <div className='row-right'>
          <div className='row-values'>
            <div className='row-value'>{valueFormatter(indirectValue)}</div>
          </div>
          {hasScenario && (
            <div className='row-pill'>{makePercentPill(indirectValue, baselineValue)}</div>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className='body'>
      {hasScenario && (
        <div className='actualized-row primary'>
          <div className='row-left'>
            <div className='row-label'>
              {showActualized ? 'Actualized' : 'Scenario'} {metricName}
            </div>
          </div>
          <div className='row-right'>
            <div className='row-values'>
              <div className='row-value'>
                {valueFormatter(showActualized ? actualizedValue : directValue)}
              </div>
              {confidenceIntervalArr && (
                <div className='confidence-interval'>
                  CI:{' '}
                  {valueFormatter(
                    (showActualized ? actualizedValue : directValue) - confidenceIntervalArr[0]
                  )}
                  –
                  {valueFormatter(
                    (showActualized ? actualizedValue : directValue) + confidenceIntervalArr[1]
                  )}
                </div>
              )}
            </div>
            <div className='row-pill'>{makePercentPill(displayedChange, baselineValue)}</div>
          </div>
        </div>
      )}
      <div className={`actualized-row${!hasScenario ? ' primary' : ''}`}>
        <div className='row-left'>
          <div className='row-label'>BAU {metricName}</div>
        </div>
        <div className='row-right'>
          <div className='row-values'>
            <div className='row-value'>{valueFormatter(baselineValue)}</div>
          </div>
          {hasScenario && <div className='row-pill' />}
        </div>
      </div>
    </div>
  );

  // ==== HROAS: per-model revenue segments, with the same shade as the bar's stack. ====
  const segmentLayout = getSegmentLayout(data);
  const segmentRows = segmentLayout && (
    <div className='model-segments'>
      <div className='model-segments-title'>By model</div>
      {segmentLayout.segments.map((segment, index) => (
        <div key={segment.id} className='model-segment-row'>
          <div
            className='model-segment-swatch'
            style={{ backgroundColor: segmentLayout.fills[index] }}
          />
          <div className='model-segment-label'>{segment.label}</div>
          <div className='model-segment-value'>
            {valueFormatter(showActualized ? segment.actualized : segment.direct)}
          </div>
          <div className='model-segment-share'>
            {Math.round(segmentLayout.fractions[index] * 100)}%
          </div>
        </div>
      ))}
    </div>
  );

  const footer = (
    <div className='footer'>
      <div className='icon-text-container'>
        <div className='icon' style={{ color }}>
          <MdCalendarToday />
        </div>
        <div className='text'>
          {metric === SCENARIO_METRIC_ENUM.SPEND
            ? `${
                modelType === MODEL_TYPES.R_MMM && !showActualized ? 'Direct ' : ''
              }Spend Flighting: ${useHistoricalFlighting ? 'Historical' : 'Even'}`
            : 'Revenue Over Time:'}
        </div>
      </div>
      <div className='line-container'>
        <FlightingSparkChart
          color={color}
          data={buildSparkChartData(flightingData, metric, useHistoricalFlighting)}
          useHistoricalFlighting={useHistoricalFlighting}
        />
      </div>
    </div>
  );

  return (
    <div className='scenario-planning-tooltip'>
      {header}
      {body}
      {segmentRows}
      {footer}
    </div>
  );
};
